
import { Dumbbell, Clock } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExerciseImage } from "@/components/exercises/ExerciseImage";
import { Exercise } from "@/components/workouts/exercise/types/ExerciseTypes";
import { useTranslations } from "@/hooks/useTranslations";

interface ExercisesTableProps {
  exercises: Exercise[];
}

// Helper function to determine if an exercise is likely a cardio exercise
const isCardioExercise = (exercise: Exercise) => {
  const cardioKeywords = [
    'correr', 'corrida', 'running', 'cardio', 'aeróbico', 'aerobic', 'pular corda', 'jump rope', 
    'esteira', 'treadmill', 'bicicleta', 'bike', 'cycling', 'elíptico', 'elliptical', 'nadar', 'natação',
    'swimming', 'caminhada', 'walking', 'step', 'hiit', 'circuito', 'circuit'
  ];
  
  const lowerName = exercise.name.toLowerCase();
  return cardioKeywords.some(keyword => lowerName.includes(keyword.toLowerCase())) || 
         (exercise.body_part && exercise.body_part.toLowerCase() === 'cardio');
};

export const ExercisesTable = ({ exercises }: ExercisesTableProps) => {
  const { t } = useTranslations("training-sessions");

  return (
    <div className="rounded-lg border border-slate-200 dark:border-gray-700 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50 dark:bg-gray-800/50">
            <TableHead className="w-[80px] dark:text-white">{t("form.image")}</TableHead>
            <TableHead className="dark:text-white">{t("form.exercise")}</TableHead>
            <TableHead className="w-[100px] text-right dark:text-white">{t("form.sets")}</TableHead>
            <TableHead className="w-[100px] text-right dark:text-white">{t("form.reps")}</TableHead>
            <TableHead className="w-[100px] text-right dark:text-white">{t("form.metric")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {exercises.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8 text-muted-foreground dark:text-gray-400">
                {t("form.noExercises")}
              </TableCell>
            </TableRow>
          ) : (
            exercises.map((exercise, index) => {
              const isCardio = isCardioExercise(exercise);
              
              return (
                <TableRow key={exercise.id?.toString() || `${exercise.name}-${index}`} className="dark:border-gray-700">
                  <TableCell className="align-middle">
                    {exercise.isCustom ? (
                      <ExerciseImage 
                        imagePath={exercise.image_url} 
                        altText={exercise.name} 
                        fallbackText={exercise.name}
                        isCustomExercise={true} 
                        className="w-16 h-16 object-cover"
                      />
                    ) : exercise.gif_url ? (
                      <ExerciseImage 
                        imagePath={exercise.gif_url} 
                        altText={exercise.name} 
                        fallbackText={exercise.name}
                        isCustomExercise={false} 
                        className="w-16 h-16 object-cover"
                      />
                    ) : exercise.image_url ? (
                      <ExerciseImage 
                        imagePath={exercise.image_url} 
                        altText={exercise.name} 
                        fallbackText={exercise.name}
                        isCustomExercise={false} 
                        className="w-16 h-16 object-cover"
                      />
                    ) : (
                      <div className="bg-slate-100 dark:bg-gray-700 rounded-md flex items-center justify-center w-16 h-16">
                        {isCardio ? (
                          <Clock className="h-8 w-8 text-slate-400 dark:text-gray-500" />
                        ) : (
                          <Dumbbell className="h-8 w-8 text-slate-400 dark:text-gray-500" />
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium dark:text-white align-middle">
                    {exercise.name}
                  </TableCell>
                  <TableCell className="text-right dark:text-white align-middle">{exercise.sets}</TableCell>
                  <TableCell className="text-right dark:text-white align-middle">{exercise.reps}</TableCell>
                  <TableCell className="text-right dark:text-white align-middle">
                    {isCardio ? (
                      <div className="flex items-center justify-end gap-1">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span>{exercise.time || "—"}</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <Dumbbell className="h-4 w-4 text-slate-400" />
                        <span>{exercise.weight || "—"}</span>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};
